import { ROOMS_ENDPOINTS } from "../../config/apiConfig";
import { getAuthToken } from "../../utils/apiUtils";

/**
 * Room File Service
 * Handles file uploads, downloads, versioning, and search for research rooms
 */

/**
 * Step 1: Get upload URL from backend
 */
export async function getUploadUrl(roomId, fileData) {
  try {
    const token = getAuthToken();
    const response = await fetch(
      ROOMS_ENDPOINTS.ATTACHMENTS.UPLOAD_URL(roomId),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(fileData),
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
throw error;
  }
}

/**
 * Step 2: Upload file directly to S3
 */
export async function uploadToS3(uploadUrl, file, mimeType, onProgress) {
  try {
    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      // Progress tracking
      if (onProgress) {
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            onProgress(percentComplete);
          }
        });
      }

      xhr.addEventListener("load", () => {
        if (xhr.status === 200) {
          resolve({ success: true });
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("Content-Type", mimeType);
      xhr.send(file);
    });
  } catch (error) {
throw error;
  }
}

/**
 * Step 3: Confirm upload with backend
 */
export async function confirmUpload(roomId, attachmentData) {
  try {
    const token = getAuthToken();
    const response = await fetch(ROOMS_ENDPOINTS.ATTACHMENTS.CREATE(roomId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(attachmentData),
    });

    const data = await response.json();
    return data;
  } catch (error) {
throw error;
  }
}

/**
 * Complete upload flow (all 3 steps)
 */
export async function uploadFile(roomId, file, options = {}, onProgress) {
  try {
    // Step 1: Get upload URL
    const urlResponse = await getUploadUrl(roomId, {
      fileName: file.name,
      mimeType: file.type || "application/octet-stream",
      fileSize: file.size,
      messageId: options.messageId,
    });

    if (!urlResponse.success) {
      throw new Error(urlResponse.message || "Failed to get upload URL");
    }

    const { uploadUrl, key, fileType } = urlResponse.data;

    // Step 2: Upload to S3
    await uploadToS3(uploadUrl, file, file.type, onProgress);

    // Step 3: Confirm upload
    const confirmResponse = await confirmUpload(roomId, {
      fileName: file.name,
      fileType,
      mimeType: file.type || "application/octet-stream",
      fileSize: file.size,
      s3Key: key,
      messageId: options.messageId,
      description: options.description,
      tags: options.tags || [],
      category: options.category,
    });

    return confirmResponse;
  } catch (error) {
throw error;
  }
}

/**
 * Get attachments list
 */
export async function getAttachments(roomId, filters = {}) {
  try {
    const token = getAuthToken();
    const params = new URLSearchParams({
      page: filters.page || "1",
      limit: filters.limit || "20",
      ...(filters.fileType && { fileType: filters.fileType }),
      ...(filters.category && { category: filters.category }),
    });

    const response = await fetch(
      `${ROOMS_ENDPOINTS.ATTACHMENTS.LIST(roomId)}?${params}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
throw error;
  }
}

/**
 * Get single attachment details
 */
export async function getAttachmentDetails(roomId, attachmentId) {
  try {
    const token = getAuthToken();
    const response = await fetch(
      ROOMS_ENDPOINTS.ATTACHMENTS.DETAIL(roomId, attachmentId),
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
throw error;
  }
}

/**
 * Download file (get signed URL)
 */
export async function downloadFile(roomId, attachmentId) {
  try {
    const token = getAuthToken();
    const response = await fetch(
      ROOMS_ENDPOINTS.ATTACHMENTS.DOWNLOAD(roomId, attachmentId),
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const data = await response.json();

    if (data.success) {
      // Trigger browser download
      const link = document.createElement("a");
      link.href = data.data.downloadUrl;
      link.download = data.data.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    return data;
  } catch (error) {
throw error;
  }
}

/**
 * Get file versions
 */
export async function getFileVersions(roomId, attachmentId) {
  try {
    const token = getAuthToken();
    const response = await fetch(
      ROOMS_ENDPOINTS.ATTACHMENTS.VERSIONS(roomId, attachmentId),
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
throw error;
  }
}

/**
 * Search files
 */
export async function searchFiles(roomId, query, limit = 20) {
  try {
    const token = getAuthToken();
    const response = await fetch(
      `${ROOMS_ENDPOINTS.ATTACHMENTS.SEARCH(roomId)}?q=${encodeURIComponent(
        query
      )}&limit=${limit}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
throw error;
  }
}

/**
 * Update attachment metadata
 */
export async function updateAttachment(roomId, attachmentId, updates) {
  try {
    const token = getAuthToken();
    const response = await fetch(
      ROOMS_ENDPOINTS.ATTACHMENTS.DETAIL(roomId, attachmentId),
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
throw error;
  }
}

/**
 * Delete attachment
 */
export async function deleteAttachment(roomId, attachmentId) {
  try {
    const token = getAuthToken();
    const response = await fetch(
      ROOMS_ENDPOINTS.ATTACHMENTS.DETAIL(roomId, attachmentId),
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
throw error;
  }
}

export default {
  uploadFile,
  getUploadUrl,
  uploadToS3,
  confirmUpload,
  getAttachments,
  getAttachmentDetails,
  downloadFile,
  getFileVersions,
  searchFiles,
  updateAttachment,
  deleteAttachment,
};

